import { useCallback, useState } from "react";

import type { BasicCommunityMember } from "@/react/types/CommunityMember";

// Only one composer in a section holds focus at a time, so a single target is
// the source of truth for who owns it. "top-level" is the top-level composer;
// "reply" is a thread's reply composer, keyed by the thread's top-level comment
// id so multiple open reply forms in a list don't all claim the same focus
// request. The reply target also carries `mentionCommunityMember`: the member,
// if any, to seed as an @mention.
export type ComposerFocusTarget =
  | { kind: "top-level" }
  | {
      kind: "reply";
      commentId: number;
      mentionCommunityMember?: BasicCommunityMember | null;
    };

export type ReplyFocusTarget = Extract<ComposerFocusTarget, { kind: "reply" }>;

export const useComposerFocusRequest = () => {
  const [composerFocusTarget, setComposerFocusTarget] =
    useState<ComposerFocusTarget | null>(null);

  const requestComposerFocus = useCallback(
    (target: ComposerFocusTarget) => setComposerFocusTarget(target),
    [],
  );

  const clearComposerFocusRequest = useCallback(
    () => setComposerFocusTarget(null),
    [],
  );

  const isTopLevelFocusPending = composerFocusTarget?.kind === "top-level";

  const matchReplyFocusTarget = useCallback(
    (commentId: number): ReplyFocusTarget | null =>
      composerFocusTarget?.kind === "reply" &&
      composerFocusTarget.commentId === commentId
        ? composerFocusTarget
        : null,
    [composerFocusTarget],
  );

  return {
    requestComposerFocus,
    clearComposerFocusRequest,
    isTopLevelFocusPending,
    matchReplyFocusTarget,
  };
};
